import { Fragment, useEffect, useMemo, useState } from "react";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../stores/hooks/hooks.ts";
import {
  deleteBrand,
  fetchBrandList,
} from "../../../../../stores/thunks/brandThunk.tsx";
import campaignEmpty from "/images/empty_states/campaign-empty.svg";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { formatDateToMonthDayYear } from "../../../../../helpers/functions/formatDate.tsx";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { Link, useParams } from "react-router";
import { BrandItemCompanyDataType } from "../../../../../stores/types/companyTypes.tsx";
import {
  RevButton,
  RevCard,
  RevDialog,
  RevEmptyPage,
  RevLink,
  RevSpinner,
  RevTooltip,
  RevActionButton,
  RevTable,
  type RevTablePaginationType,
} from "@revmatics/catalyst";
import { useFlags } from "launchdarkly-react-client-sdk";

const brandColumnHelper = createColumnHelper<BrandItemCompanyDataType>();
const BRANDS_SAVED_TABLE = "brandsTable";

type Props = {
  brandList?: BrandItemCompanyDataType[];
  isLoading?: boolean;
  setIsOpenAddBrand: (isAddBrand: boolean) => void;
};

export const BrandsView = ({
  brandList,
  isLoading,
  setIsOpenAddBrand,
}: Props) => {
  const dispatch = useAppDispatch();
  const { companyId } = useParams();
  const {
    addBrand: ffAddBrand,
    brandDetails: ffBrandDetails,
    deleteBrand: ffDeleteBrand,
  } = useFlags();
  const profile = useAppSelector((state) => state.profile.profile);
  const isLoadingDelete = useAppSelector(
    (state) => state.brand.isLoadingDelete,
  );

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] =
    useState<BrandItemCompanyDataType | null>(null);
  useEffect(() => {
    if (!brandList) {
      dispatch(fetchBrandList());
    }
  }, [dispatch, brandList]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns = useMemo<ColumnDef<BrandItemCompanyDataType, any>[]>(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const baseColumns: ColumnDef<BrandItemCompanyDataType, any>[] = [
      brandColumnHelper.accessor("name", {
        id: "brandName",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs text-monochrome-900">
            Brand Name
          </span>
        ),
        cell: (info) => {
          const originalData = info.row.original;
          return ffBrandDetails ? (
            <RevLink
              pathname={`brand/${originalData.id}/`}
              text={originalData.name}
              className="w-fit"
            />
          ) : (
            <span>{originalData.name}</span>
          );
        },
        enableSorting: true,
        size: 300,
      }),
      brandColumnHelper.accessor("created_at", {
        id: "createdOn",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs text-monochrome-900">
            Created on
          </span>
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <span className="whitespace-nowrap typography-body-sm text-monochrome-900">
              {formatDateToMonthDayYear(value)}
            </span>
          );
        },
        enableSorting: true,
        size: 180,
      }),
      brandColumnHelper.accessor("id", {
        id: "id",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs text-monochrome-900">
            Brand ID
          </span>
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <span className="whitespace-nowrap typography-body-sm text-monochrome-900">
              {value}
            </span>
          );
        },
        enableSorting: true,
        size: 180,
      }),
      brandColumnHelper.accessor("website", {
        id: "website",
        header: () => (
          <span className="whitespace-nowrap typography-body-bold-xs text-monochrome-900">
            Website
          </span>
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <span className="whitespace-nowrap typography-body-sm text-monochrome-900">
              {value}
            </span>
          );
        },
        enableSorting: true,
        size: 180,
      }),
    ];
    const actionColumn = brandColumnHelper.display({
      id: "actions",
      header: () => <span></span>,
      cell: (info) => {
        const originalData = info.row.original;
        return (
          <div className="flex items-center justify-center gap-1">
            <Menu>
              {({ open }) => {
                return (
                  <div className="relative">
                    {!open ? (
                      <RevTooltip
                        content="More Actions"
                        position="left"
                        className="w-[90px]"
                      >
                        <MenuButton
                          data-testid="more-actions"
                          as={RevActionButton}
                        />
                      </RevTooltip>
                    ) : (
                      <MenuButton as={RevActionButton} />
                    )}

                    <MenuItems
                      anchor={{ to: "bottom end", gap: 4 }}
                      transition
                      className="z-20 flex w-[200px] origin-top flex-col rounded-lg bg-white py-2 shadow-elevation-2 outline-0 transition duration-200 ease-out data-closed:scale-95 data-closed:opacity-0"
                    >
                      {ffBrandDetails && (
                        <MenuItem as={Fragment}>
                          <Link
                            to={`brand/${originalData.id}`}
                            className="w-full px-3 py-2 text-start typography-body-sm text-monochrome-900 transition-colors hover:bg-monochrome-50 disabled:text-monochrome-400"
                          >
                            View / Edit Brand
                          </Link>
                        </MenuItem>
                      )}

                      {!companyId && ffDeleteBrand && (
                        <MenuItem as={Fragment}>
                          <button
                            onClick={() => setIsDeleteDialogOpen(originalData)}
                            aria-label="delete brand"
                            className="w-full px-3 py-2 text-start typography-body-sm text-red-600 transition-colors hover:bg-monochrome-50 disabled:text-monochrome-400"
                          >
                            Delete Brand
                          </button>
                        </MenuItem>
                      )}
                    </MenuItems>
                  </div>
                );
              }}
            </Menu>
          </div>
        );
      },
      size: 48,
    });
    return [
      ...baseColumns,
      ...(ffBrandDetails || ffDeleteBrand ? [actionColumn] : []),
    ];
  }, [companyId, ffBrandDetails, ffDeleteBrand]);

  const saved: RevTablePaginationType = JSON.parse(
    sessionStorage.getItem(BRANDS_SAVED_TABLE) || "{}",
  );

  if (isLoading) {
    return (
      <div className="h-full">
        <RevSpinner size="large" color="primary" centered />
      </div>
    );
  }
  if (!isLoading && brandList && brandList.length === 0) {
    return ffAddBrand &&
      (profile?.roles.includes("ORGANIZATION_ADMIN") ||
        profile?.roles.includes("INTERNAL_CUSTOMER_ONBOARDING")) ? (
      <RevEmptyPage
        image={campaignEmpty}
        title="No Brand"
        text="Let's create your first brand now!"
        to={"brand/create/"}
        textBtn="Create Brand"
      />
    ) : (
      <RevEmptyPage
        image={campaignEmpty}
        title="No Brand"
        text="Let's create your first brand now!"
        handleClick={() => setIsOpenAddBrand(true)}
        textBtn="Create Brand"
      />
    );
  }

  return (
    <RevCard padding="p-0">
      <RevTable<BrandItemCompanyDataType>
        data={brandList || []}
        columns={columns}
        initialPageSize={saved.pageSize || 10}
        initialPageIndex={saved.pageIndex || 0}
        enableGlobalFilter={false}
        enablePagination={true}
        paginationSaved={BRANDS_SAVED_TABLE}
      />
      <RevDialog
        isOpen={!!isDeleteDialogOpen}
        setIsOpen={() => setIsDeleteDialogOpen(null)}
        title="Delete Brand?"
        btnList={[
          <RevButton
            key="cancel-brand-delete"
            text="Cancel"
            variant="secondary"
            handleClick={() => setIsDeleteDialogOpen(null)}
          />,
          <RevButton
            key="delete-brand-delete"
            text="Yes, delete"
            variant="primary"
            buttonColor="error"
            handleClick={() => {
              if (isDeleteDialogOpen) {
                dispatch(
                  deleteBrand({
                    id: isDeleteDialogOpen.id.toString(),
                    name: isDeleteDialogOpen.name,
                    callback: () => setIsDeleteDialogOpen(null),
                  }),
                );
              }
            }}
            isLoading={isLoadingDelete}
          />,
        ]}
      >
        <p className="typography-body-sm text-monochrome-900">
          Are you sure you want to delete the brand "{isDeleteDialogOpen?.name}
          "? This action can't be undone.
        </p>
      </RevDialog>
    </RevCard>
  );
};
